import React from "react";
import { Controller, Control, UseFormSetValue, useWatch } from "react-hook-form";
import { Input as AntInput, InputNumber, Select as AntSelect, Checkbox, Cascader } from "antd";
import TinyMCE from "../../../../utils/TinyMCE";
import ApiSelect from "../../../components/ApiSelect";
import UploadImage from "../../../components/UploadImage";
import ImageAltEditor from "../../../components/ImageAltEditor";
import { IProduct, ProductFormValues } from "../../../../types/api/product";
import { useMasterCreate } from "../../../../hooks/api/master/hooks";
import { removeSpecialChars } from "../../../../lib/utils";
import { useMessageApi } from "../../../../provider/AntdNotification";

interface BasicInfoTabProps {
  control: Control<ProductFormValues>;
  setValue: UseFormSetValue<ProductFormValues>;
  selectedItem?: IProduct | null;
  artistOptions?: any[];
  artistsData?: any;
  refetchArtist: (query: any) => void;
  categoryOptions?: any[];
  categoryData?: any;
  // Doc-shaped pool (keeps parentId) used for the Category/Sub Category
  // split - unlike categoryData alone, this always includes whatever
  // categories the loaded product itself references, even ones since
  // soft-deleted or otherwise missing from the general fetched list. See
  // ProductModal.tsx's categoryPool memo.
  categoryPool?: any[];
  refetchCategory?: (query: any) => void;
  subjectOptions?: any[];
  subjectsData?: any;
  refetchSubject: (query: any) => void;
  styleOptions?: any[];
  stylesData?: any;
  refetchStyle: (query: any) => void;
  tagsOptions?: any[];
  tagsData?: any;
  refetchTag?: (query: any) => void;
  materialOptions?: any[];
  materialData?: any;
  refetchMaterial?: (query: any) => void;
}

const { TextArea } = AntInput;

export const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  control,
  setValue,
  selectedItem,
  artistOptions,
  artistsData,
  refetchArtist,
  categoryOptions,
  categoryData,
  categoryPool,
  refetchCategory,
  subjectOptions,
  subjectsData,
  refetchSubject,
  styleOptions,
  stylesData,
  refetchStyle,
  tagsOptions,
  tagsData,
  refetchTag,
  materialOptions,
  materialData,
  refetchMaterial,
}) => {
  const messageApi = useMessageApi();

  const mergedArtistOptions = artistOptions || artistsData?.data?.map((artist: any) => ({
    label: artist.name,
    value: artist._id,
  }));
  const mergedMaterialOptions = materialOptions || materialData?.data?.map((m: any) => ({
    label: m.name,
    value: m._id,
  }));

  // Peter, Oct 2026: "Can we just add in Tag words relevant to the piece
  // in question" rather than detouring through Masters first - see
  // ApiSelect's own allowCreate comment. No qData here since this list's
  // own refetch (refetchTag below) already brings a freshly-created tag
  // back into the dropdown's results.
  const createTagMutation = useMasterCreate({});
  const handleCreateTag = async (name: string) => {
    // slug is a required field on Master create (masterValidations.create)
    // and also checked for uniqueness per type server-side - a plain-name
    // POST 422'd silently every time until this was added. A numeric
    // suffix on an actual collision is enough here (unlike Product/
    // Category slugs, a Tag's slug is never shown anywhere - it only
    // exists to satisfy this uniqueness check).
    const baseSlug = removeSpecialChars(name).toLowerCase();
    let slug = baseSlug;
    let attempt = 1;
    while (true) {
      try {
        const res = await createTagMutation.mutateAsync({
          name,
          slug,
          type: "TAGS" as any,
        });
        const created = (res as any).data?.data;
        return { label: created.name, value: created._id };
      } catch (err: any) {
        const message = err?.response?.data?.message || err?.message || "";
        if (/slug.*already exists/i.test(message) && attempt < 5) {
          attempt += 1;
          slug = `${baseSlug}-${attempt}`;
          continue;
        }
        throw err;
      }
    }
  };

  const watchVariantImages = useWatch({ control, name: "variant.images" });
  const watchIsForSale = useWatch({ control, name: "variant.isForSale" });
  const watchIsPOA = useWatch({ control, name: "variant.isPriceOnApplication" });
  const watchArtistProofsAvailable = useWatch({ control, name: "variant.artistProofsAvailable" });

  // "Is this item for sale?" - yes / price on application / not for sale,
  // matching the artist-portal sculpture form's three options exactly
  // (Oliver, Sept 2026: "Could Edit Product work like the artist form").
  const saleStatus = watchIsForSale === false ? "not_for_sale" : watchIsPOA ? "poa" : "for_sale";
  const setSaleStatus = (val: string) => {
    setValue("variant.isForSale", val !== "not_for_sale");
    setValue("variant.isPriceOnApplication", val === "poa");
  };

  // Artist Price is 60% of the website price (25% ArtParkS commission +
  // 15% shipping make up the other 40%) - same relationship as the
  // artist-portal form, filling in either box works out the other.
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const onArtistPriceChange = (val: number) => {
    setValue("variant.artistPrice", val);
    setValue("variant.price", round2(val / 0.6));
  };
  const onWebsitePriceChange = (val: number) => {
    setValue("variant.price", val);
    setValue("variant.artistPrice", round2(val * 0.6));
  };

  // Full category pool (mains + subs together) - categoryPool (falling
  // back to the raw fetched list if a caller doesn't pass it) rather than
  // categoryData directly, so a category this specific product references
  // but that's fallen out of the general fetched list (soft-deleted, or
  // simply missing from the first page) still resolves to a real,
  // selectable option instead of a value with no matching label (Frankie
  // via Oliver, Sept 2026 - see ProductModal.tsx's categoryPool memo).
  // Split into "Category" (parentId: null) and "Sub Category" (has a
  // parentId) so the two dropdowns are each a real, locked-to-existing-
  // data list rather than one flat free-search-everything select that
  // mixed mains and subs together.
  const allCategories: any[] = categoryPool || categoryData?.data || [];
  const mainCategoryPool = allCategories.filter((c: any) => !c.parentId);
  const subCategoryPool = allCategories.filter((c: any) => !!c.parentId);

  const mainCategoryOptions = mainCategoryPool.map((cat: any) => ({
    label: cat.name,
    value: cat._id,
  }));

  const selectedMainCategoryIds: string[] = useWatch({ control, name: "categoryIds" }) || [];
  const selectedSubCategoryIds: string[] = useWatch({ control, name: "subCategoryIds" }) || [];

  // Frankie's real taxonomy reuses the same sub-category name under
  // several different mains on purpose (e.g. "Lifelike" is its own real
  // node under Animal, Birds, Human, and 4 others - not a duplicate).
  // With more than one main selected, the merged list could show
  // "Lifelike" several times with nothing to tell them apart (Peter,
  // Sept 2026 - "all are in duplicate... I think lifelike appears in
  // both Category and sub category"). Naming each option's parent makes
  // clear they're distinct real entries, not the same thing repeated.
  const mainNameById = new Map(mainCategoryPool.map((cat: any) => [String(cat._id), cat.name]));
  const subCategoryOptionsForSelectedMains = subCategoryPool
    .filter((cat: any) => {
      const parentId = typeof cat.parentId === "object" ? cat.parentId?._id : cat.parentId;
      return selectedMainCategoryIds.includes(String(parentId));
    })
    .map((cat: any) => {
      const parentId = typeof cat.parentId === "object" ? cat.parentId?._id : cat.parentId;
      const parentName = mainNameById.get(String(parentId));
      return {
        label: parentName ? `${cat.name} (${parentName})` : cat.name,
        value: cat._id,
      };
    });

  const mergedSubjectOptions = subjectOptions || subjectsData?.data?.map((item: any) => ({
    label: item.name,
    value: item._id,
  }));

  const mergedStyleOptions = styleOptions || stylesData?.data?.map((item: any) => ({
    label: item.name,
    value: item._id,
  }));

  const mergedTagsOptions = tagsOptions || tagsData?.data?.map((tag: any) => ({
    label: tag.name,
    value: tag._id,
  }));

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="title"
          control={control}
          rules={{ required: "Title is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <AntInput
                {...field}
                placeholder="Enter title"
                status={fieldState.error ? "error" : ""}
                onChange={(e) => {
                  field.onChange(e);
                  if (!selectedItem) {
                    setValue(
                      "slug",
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/(^-|-$)+/g, ""),
                    );
                  }
                }}
              />
              {fieldState.error && (
                <p className="text-red-500 text-xs mt-1">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />
        <Controller
          name="slug"
          control={control}
          rules={{ required: "Slug is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">
                Slug <span className="text-red-500">*</span>
              </label>
              <AntInput
                {...field}
                placeholder="sculpture-slug"
                status={fieldState.error ? "error" : ""}
              />
              {fieldState.error && (
                <p className="text-red-500 text-xs mt-1">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />
      </div>

      <Controller
        name="artistId"
        control={control}
        rules={{ required: "Artist is required" }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium mb-1">
              Artist <span className="text-red-500">*</span>
            </label>
            <ApiSelect
              {...field}
              className="w-full"
              placeholder="Select Artist"
              options={mergedArtistOptions}
              status={fieldState.error ? "error" : ""}
              refetchWithQuery={refetchArtist}
              baseQuery={{ limit: 100, page: 1 }}
            />
            {fieldState.error && (
              <p className="text-red-500 text-xs mt-1">
                {fieldState.error.message}
              </p>
            )}
          </div>
        )}
      />

      {/* Peter, Oct 2026: "there used to be a place to say where the
          sculpture is for ie Indoors or Outside or both i cant find it" -
          Display was never removed, just pushed further and further down
          the tab as Category/Tags/Subject/Style/SEO were added above it
          over several rounds of earlier changes this session, until it
          sat second-to-last before Artist's Comment. Moved up here with
          Featured/Active - alongside the sculpture's basic identity
          rather than buried under everything added since. */}
      <div className="grid grid-cols-3 gap-4 border-t pt-4">
        <Controller
          name="display"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Display</label>
              <AntSelect value={value} onChange={onChange} className="w-full">
                <AntSelect.Option value="Indoor">Indoor</AntSelect.Option>
                <AntSelect.Option value="Outdoor">Outdoor</AntSelect.Option>
                <AntSelect.Option value="Both">Both</AntSelect.Option>
              </AntSelect>
            </div>
          )}
        />
        <Controller
          name="isFeatured"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox
              checked={value}
              onChange={(e) => onChange(e.target.checked)}
              className="mt-6"
            >
              Featured Sculpture
            </Checkbox>
          )}
        />
        <Controller
          name="status"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox
              checked={value}
              onChange={(e) => onChange(e.target.checked)}
              className="mt-6"
            >
              Active / Approved (live on the site)
            </Checkbox>
          )}
        />
      </div>

      {/* PHOTOS, PRICE, SIZE & MATERIAL - the sculpture's first (default)
          edition, on the same window as the title rather than two steps
          further under Editions (Oliver, Sept 2026 - matches the
          artist-portal sculpture form's "Price, Size & Edition" section).
          Further editions/sizes beyond this first one are still added via
          the Editions tab. */}
      <div className="space-y-1.5">
        <label className="block text-sm font-medium mb-1">Photographs</label>
        <Controller
          name="variant.images"
          control={control}
          render={({ field }) => (
            <UploadImage
              value={field.value as unknown as string}
              count={20}
              listType="picture-card"
              onChange={(urls) => field.onChange(urls)}
            />
          )}
        />
        <Controller
          name="variant.imageAlts"
          control={control}
          render={({ field }) => (
            <ImageAltEditor
              images={watchVariantImages || []}
              alts={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
        <p className="text-sm font-semibold text-slate-800">
          Price, Size &amp; Edition
        </p>
        <p className="text-xs text-slate-500 -mt-2">
          Covers this sculpture&apos;s first (default) edition - add
          further sizes or editions afterwards via the Editions tab.
        </p>

        <div>
          <label className="block text-sm font-medium mb-1">Is this item for sale?</label>
          <AntSelect
            value={saleStatus}
            onChange={setSaleStatus}
            className="w-full"
            options={[
              { label: "Yes", value: "for_sale" },
              { label: "Price on Application", value: "poa" },
              { label: "No", value: "not_for_sale" },
            ]}
          />
        </div>

        {saleStatus === "for_sale" && (
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="variant.artistPrice"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="block text-sm font-medium mb-1">Artist Price</label>
                  <p className="text-xs text-slate-500 -mt-0.5 mb-1">(60% of website price)</p>
                  <InputNumber
                    className="w-full"
                    min={0}
                    step={0.01}
                    value={field.value}
                    onChange={(val) => onArtistPriceChange(Number(val) || 0)}
                  />
                </div>
              )}
            />
            <Controller
              name="variant.price"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="block text-sm font-medium mb-1">Price shown on the website</label>
                  <p className="text-xs text-slate-500 -mt-0.5 mb-1">(Includes 25% ArtParkS commission and 15% shipping)</p>
                  <InputNumber
                    className="w-full"
                    min={0}
                    step={0.01}
                    value={field.value}
                    onChange={(val) => onWebsitePriceChange(Number(val) || 0)}
                  />
                </div>
              )}
            />
          </div>
        )}

        <Controller
          name="variant.materialId"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Medium / Material</label>
              <ApiSelect
                {...field}
                className="w-full"
                placeholder="Select Medium / Material"
                options={mergedMaterialOptions}
                refetchWithQuery={refetchMaterial}
                baseQuery={{ limit: 200, page: 1, type: "MEDIUM" }}
              />
            </div>
          )}
        />

        <p className="text-sm font-semibold text-slate-800 pt-1">Dimensions (Height × Width × Depth)</p>
        <div className="grid grid-cols-4 gap-2">
          <Controller
            name="variant.dimensions.height"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-xs text-slate-600 mb-1">Height</label>
                <InputNumber className="w-full" min={0} step={0.1} value={field.value} onChange={(val) => field.onChange(Number(val) || 0)} />
              </div>
            )}
          />
          <Controller
            name="variant.dimensions.width"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-xs text-slate-600 mb-1">Width</label>
                <InputNumber className="w-full" min={0} step={0.1} value={field.value} onChange={(val) => field.onChange(Number(val) || 0)} />
              </div>
            )}
          />
          <Controller
            name="variant.dimensions.depth"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-xs text-slate-600 mb-1">Depth</label>
                <InputNumber className="w-full" min={0} step={0.1} value={field.value} onChange={(val) => field.onChange(Number(val) || 0)} />
              </div>
            )}
          />
          <Controller
            name="variant.dimensions.unit"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-xs text-slate-600 mb-1">Unit</label>
                <AntSelect
                  value={field.value}
                  onChange={field.onChange}
                  className="w-full"
                  options={[
                    { label: "cm", value: "cm" },
                    { label: "inches", value: "inches" },
                    { label: "mm", value: "mm" },
                    { label: "m", value: "m" },
                  ]}
                />
              </div>
            )}
          />
        </div>

        <p className="text-sm font-semibold text-slate-800 pt-1">Weight</p>
        <div className="grid grid-cols-2 gap-3">
          <Controller
            name="variant.weight.value"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-xs text-slate-600 mb-1">Value</label>
                <InputNumber className="w-full" min={0} step={0.1} value={field.value} onChange={(val) => field.onChange(Number(val) || 0)} />
              </div>
            )}
          />
          <Controller
            name="variant.weight.unit"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-xs text-slate-600 mb-1">Unit</label>
                <AntSelect
                  value={field.value}
                  onChange={field.onChange}
                  className="w-full"
                  options={[
                    { label: "kg", value: "kg" },
                    { label: "lbs", value: "lbs" },
                    { label: "g", value: "g" },
                  ]}
                />
              </div>
            )}
          />
        </div>

        <div className="grid grid-cols-3 gap-4 pt-1">
          <Controller
            name="variant.editionText"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Edition</label>
                <AntInput {...field} placeholder="e.g. 1 of 12" />
              </div>
            )}
          />
          <Controller
            name="variant.year"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Creation Year</label>
                <InputNumber
                  className="w-full"
                  placeholder="e.g. 2026"
                  value={field.value ?? undefined}
                  onChange={(val) => field.onChange(val ?? null)}
                />
              </div>
            )}
          />
          <Controller
            name="variant.isCommissioned"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-6">
                This piece was commissioned
              </Checkbox>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="variant.artistProofsAvailable"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox checked={!!value} onChange={(e) => onChange(e.target.checked)}>
                Artist&apos;s Proofs Available
              </Checkbox>
            )}
          />
          {watchArtistProofsAvailable && (
            <Controller
              name="variant.artistProofsCount"
              control={control}
              render={({ field }) => (
                <div>
                  <label className="block text-sm font-medium mb-1">Number of Artist&apos;s Proofs</label>
                  <InputNumber className="w-full" min={0} value={field.value} onChange={(val) => field.onChange(Number(val) || 0)} />
                </div>
              )}
            />
          )}
        </div>
      </div>

      <Controller
        name="shortDescription"
        control={control}
        render={({ field }) => (
          <div>
            <label className="block text-sm font-medium mb-1">
              Short Description (Plain Text)
            </label>
            <TextArea
              {...field}
              rows={3}
              placeholder="Enter brief short description"
            />
          </div>
        )}
      />

      <Controller
        name="description"
        control={control}
        render={({ field: { value, onChange } }) => (
          <div>
            <label className="block text-sm font-medium mb-1">
              Description (Rich Text)
            </label>
            <TinyMCE value={value} onChange={onChange} />
          </div>
        )}
      />

      {/* SEO - how this sculpture's page is presented in Google search
          results, independently of the title/description above (Frankie,
          Oct 2026). Google may still rewrite either. Both optional - the
          sculpture page falls back to the title/description above when
          blank (see web/app/sculpture/[slug]/page.tsx's generateMetadata). */}
      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
        <p className="text-sm font-semibold text-slate-800">SEO</p>
        <p className="text-xs text-slate-500 -mt-2">
          Controls how this sculpture appears in Google search results.
          Google may still rewrite either field. Leave blank to fall back to
          the title and description above.
        </p>
        <Controller
          name="seoTitle"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">SEO Title</label>
              <AntInput
                {...field}
                showCount
                maxLength={60}
                placeholder="Ideally 50-60 characters"
              />
            </div>
          )}
        />
        <Controller
          name="metaDescription"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Meta Description</label>
              <TextArea
                {...field}
                showCount
                maxLength={160}
                rows={2}
                placeholder="Ideally 140-160 characters"
              />
            </div>
          )}
        />
      </div>

      {/* Category (main) + Sub Category - two locked dropdowns (pick only
          from real existing categories, no free text) instead of one flat
          list mixing mains and subs. Sub Category cascades: its options are
          scoped to whichever Category main(s) are currently selected. */}
      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="categoryIds"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <AntSelect
                {...field}
                mode="multiple"
                className="w-full"
                placeholder="Select Category"
                options={mainCategoryOptions}
                onChange={(val) => {
                  field.onChange(val);
                  // Dropping a main category invalidates any of its subs
                  // still selected - keep only subs whose parent survives.
                  const stillValidSubs = subCategoryPool
                    .filter((cat: any) => {
                      const parentId =
                        typeof cat.parentId === "object" ? cat.parentId?._id : cat.parentId;
                      return (val as string[]).includes(String(parentId));
                    })
                    .map((cat: any) => cat._id);
                  setValue(
                    "subCategoryIds",
                    selectedSubCategoryIds.filter((id) => stillValidSubs.includes(id)),
                  );
                }}
              />
            </div>
          )}
        />
        <Controller
          name="subCategoryIds"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Sub Category</label>
              <AntSelect
                {...field}
                mode="multiple"
                className="w-full"
                placeholder={
                  selectedMainCategoryIds.length === 0
                    ? "Select a Category first"
                    : "Select Sub Category"
                }
                disabled={selectedMainCategoryIds.length === 0}
                options={subCategoryOptionsForSelectedMains}
              />
            </div>
          )}
        />
      </div>

      {/* Peter, Oct 2026: "Can we just add in Tag words relevant to the
          piece in question" - a phrase like "Bronze Salomi small
          sculptures" only ever matters for this one sculpture, so making
          an admin create it under Masters first, then come back here to
          attach it, was pure friction for something this disposable.
          allowCreate offers "+ Create '<text>'" the moment what's typed
          doesn't already match a real tag - picking it creates the Master
          and attaches it in one step. Category/Subject/Style stay locked,
          curated lists elsewhere in this form on purpose (Frankie's own
          taxonomy work) - Tags was always the one meant to grow freely,
          already 57,000+ entries, mostly used on a single sculpture each. */}
      <Controller
        name="tags"
        control={control}
        render={({ field }) => (
          <div>
            <label className="block text-sm font-medium mb-1">Tags</label>
            <ApiSelect
              {...field}
              mode="multiple"
              className="w-full"
              placeholder="Search tags, or type a new one and create it"
              options={mergedTagsOptions}
              refetchWithQuery={refetchTag}
              baseQuery={{ limit: 20, page: 1, type: "TAGS" }}
              allowCreate
              onCreateOption={handleCreateTag}
              createLabel={(text) => `+ Create tag "${text}"`}
              onCreateError={(err: any) =>
                messageApi.error(
                  err?.response?.data?.message || err?.message || "Failed to create tag",
                )
              }
            />
          </div>
        )}
      />

      {/* Peter, Oct 2026: "Both Subject and style only have choices
          beginning with A." Subject has 1525 real entries, Style 1007 -
          sorted alphabetically (Frankie's own request for every dropdown
          fed by this list, so entries are quick to find by typing rather
          than scattered in creation order), limit 20 meant the unsearched
          view could never get past the 136 names starting with "A" alone.
          Typing already searches the full list correctly (confirmed live -
          not a search bug), but the empty-box default was misleading on
          lists this size. Bumped to the same 200 already used for Medium's
          own large list two fields up, rather than inventing a new
          precedent. */}
      <div className="grid grid-cols-2 gap-4">
        <Controller
          name="subjectId"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Subject</label>
              <ApiSelect
                {...field}
                className="w-full"
                placeholder="Select Subject"
                options={mergedSubjectOptions}
                refetchWithQuery={refetchSubject}
                baseQuery={{ limit: 200, page: 1, type: "SUBJECT" }}
              />
            </div>
          )}
        />
        <Controller
          name="styleId"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Style</label>
              <ApiSelect
                {...field}
                className="w-full"
                placeholder="Select Style"
                options={mergedStyleOptions}
                refetchWithQuery={refetchStyle}
                baseQuery={{ limit: 200, page: 1, type: "STYLE" }}
              />
            </div>
          )}
        />
      </div>

      {/* ARTIST'S COMMENT - public, shown on the sculpture page below the
          description, same field the artist-portal sculpture form uses. */}
      <Controller
        name="artistComment"
        control={control}
        render={({ field }) => (
          <div>
            <label className="block text-sm font-medium mb-1">
              Artist&apos;s Comment (shown publicly on the sculpture page)
            </label>
            <TextArea
              {...field}
              rows={3}
              placeholder="A personal note about this piece, shown to visitors on the sculpture page..."
            />
          </div>
        )}
      />
    </div>
  );
};
